import { useState, useRef, useCallback } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { Shield, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/hooks/useAuth';

type Mode = 'totp' | 'recovery';

export function TwoFactor() {
  const navigate = useNavigate();
  const { state, totpVerify, recoveryVerify, logout } = useAuth();
  const { t } = useTranslation('auth');
  const [mode, setMode] = useState<Mode>('totp');
  const [digits, setDigits] = useState(['', '', '', '', '', '']);
  const [recoveryCode, setRecoveryCode] = useState('');
  const [rememberDevice, setRememberDevice] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  const setRef = useCallback((index: number) => (el: HTMLInputElement | null) => {
    inputRefs.current[index] = el;
  }, []);

  // Redirect if not in TOTP verify state
  if (state.status !== 'totp_verify') {
    return <Navigate to="/login" replace />;
  }

  function handleDigitChange(index: number, value: string) {
    const cleaned = value.replace(/\D/g, '');
    if (cleaned.length > 1) {
      // Multi-character = paste fallback
      const newDigits = [...digits];
      cleaned.slice(0, 6).split('').forEach((char, i) => {
        if (i < 6) newDigits[i] = char;
      });
      setDigits(newDigits);
      inputRefs.current[Math.min(cleaned.length, 5)]?.focus();
      return;
    }
    if (!/^\d?$/.test(value)) return;
    const newDigits = [...digits];
    newDigits[index] = value;
    setDigits(newDigits);
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  }

  function handleKeyDown(index: number, e: React.KeyboardEvent) {
    if (e.key === 'Backspace' && !digits[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  }

  function handlePaste(e: React.ClipboardEvent) {
    e.preventDefault();
    const paste = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    const newDigits = [...digits];
    paste.split('').forEach((char, i) => {
      if (i < 6) newDigits[i] = char;
    });
    setDigits(newDigits);
    const nextIndex = Math.min(paste.length, 5);
    inputRefs.current[nextIndex]?.focus();
  }

  async function handleVerifyTotp(e: React.FormEvent) {
    e.preventDefault();
    const code = digits.join('');
    if (code.length < 6) return;

    setError('');
    setIsLoading(true);
    try {
      await totpVerify(code, rememberDevice);
      navigate('/dashboard');
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Verification failed');
      setDigits(['', '', '', '', '', '']);
      inputRefs.current[0]?.focus();
    } finally {
      setIsLoading(false);
    }
  }

  async function handleVerifyRecovery(e: React.FormEvent) {
    e.preventDefault();
    if (!recoveryCode.trim()) return;

    setError('');
    setIsLoading(true);
    try {
      const remaining = await recoveryVerify(recoveryCode.trim(), rememberDevice);
      if (remaining <= 2) {
        console.warn(`Only ${remaining} recovery codes remaining`);
      }
      navigate('/dashboard');
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Invalid recovery code');
      setRecoveryCode('');
    } finally {
      setIsLoading(false);
    }
  }

  async function handleBackToLogin() {
    await logout();
    navigate('/login');
  }

  return (
    <div className="min-h-dvh animated-gradient-bg relative flex items-start sm:items-center justify-center">
      {/* Top-left: Unifiber logo */}
      <div className="absolute top-4 left-4 sm:top-8 sm:left-10 z-10">
        <img src="/UnifiberLogoWhite.png" alt="Unifiber" className="h-8 sm:h-12 opacity-90" />
      </div>

      {/* Centered glass card */}
      <div className="relative z-10 px-6 pt-20 pb-6 sm:py-12 w-full max-w-md">
        <div className="glass-card-login rounded-2xl p-6 sm:p-10 space-y-6">
          {/* Shield icon with pulse */}
          <div className="flex justify-center">
            <div className="w-16 h-16 rounded-full bg-white/10 flex items-center justify-center animate-pulse">
              <Shield className="w-8 h-8 text-white" />
            </div>
          </div>

          {/* Header */}
          <div className="text-center">
            <h2 className="text-xl font-bold text-white">{t('twoFactor.title')}</h2>
            <p className="text-white/60 mt-2 text-sm leading-relaxed">
              {mode === 'totp'
                ? t('twoFactor.subtitle')
                : t('twoFactor.recoverySubtitle')}
            </p>
          </div>

          {/* ── TOTP Mode ──────────────────────────────────── */}
          {mode === 'totp' && (
            <form onSubmit={handleVerifyTotp} className="space-y-6">
              {error && (
                <div className="px-4 py-2.5 rounded-lg bg-red-500/20 border border-red-400/30 text-red-200 text-sm font-medium">
                  {error}
                </div>
              )}

              <div className="flex justify-center gap-2 sm:gap-3" role="group" aria-label={t('twoFactor.codeLabel')}>
                {digits.map((digit, i) => (
                  <span key={i} className="contents">
                    {i === 3 && (
                      <div className="flex items-center text-white/30 text-xl font-light">&ndash;</div>
                    )}
                    <input
                      ref={setRef(i)}
                      type="text"
                      inputMode="numeric"
                      autoComplete={i === 0 ? 'one-time-code' : 'off'}
                      aria-label={t('twoFactor.digitAriaLabel', { index: i + 1 })}
                      value={digit}
                      onChange={(e) => handleDigitChange(i, e.target.value)}
                      onKeyDown={(e) => handleKeyDown(i, e)}
                      onPaste={handlePaste}
                      placeholder="·"
                      disabled={isLoading}
                      className="glass-input w-10 h-12 sm:w-12 sm:h-14 text-center text-lg sm:text-xl font-bold rounded-lg text-white placeholder:text-white/25 focus:outline-none focus:border-brand-rose focus:bg-white/[0.22] focus:ring-[3px] focus:ring-brand-rose/25 caret-white disabled:opacity-50"
                    />
                  </span>
                ))}
              </div>

              {/* Remember device */}
              <div className="flex items-start gap-2">
                <input
                  type="checkbox"
                  id="remember-device"
                  checked={rememberDevice}
                  onChange={(e) => setRememberDevice(e.target.checked)}
                  className="appearance-none w-4 h-4 mt-0.5 border border-white/35 rounded bg-white/[0.12] cursor-pointer checked:bg-gradient-to-br checked:from-brand-rose checked:to-brand-orange checked:border-transparent focus:outline-none focus:ring-[3px] focus:ring-brand-rose/20 relative shrink-0
                    after:content-['✓'] after:absolute after:text-white after:text-[11px] after:top-1/2 after:left-1/2 after:-translate-x-1/2 after:-translate-y-1/2 after:opacity-0 checked:after:opacity-100"
                />
                <label htmlFor="remember-device" className="text-sm text-white/70 leading-snug">
                  {t('twoFactor.rememberDevice')}
                </label>
              </div>

              {/* Verify button */}
              <button
                type="submit"
                disabled={digits.join('').length < 6 || isLoading}
                className="w-full py-2.5 rounded-lg text-white font-semibold text-sm shadow-lg bg-gradient-to-r from-brand-rose to-brand-orange hover:from-brand-purple hover:to-brand-rose hover:-translate-y-px hover:shadow-[0_8px_25px_rgba(237,18,95,0.4)] transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0 flex items-center justify-center gap-2"
              >
                {isLoading && (
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                )}
                {isLoading ? t('twoFactor.verifying') : t('twoFactor.verify')}
              </button>
            </form>
          )}

          {/* ── Recovery Mode ──────────────────────────────── */}
          {mode === 'recovery' && (
            <form onSubmit={handleVerifyRecovery} className="space-y-6">
              {error && (
                <div className="px-4 py-2.5 rounded-lg bg-red-500/20 border border-red-400/30 text-red-200 text-sm font-medium">
                  {error}
                </div>
              )}

              <input
                type="text"
                autoComplete="one-time-code"
                value={recoveryCode}
                onChange={(e) => setRecoveryCode(e.target.value.toUpperCase())}
                placeholder={t('twoFactor.recoveryPlaceholder')}
                disabled={isLoading}
                className="glass-input w-full px-4 py-3 rounded-lg text-center text-lg font-mono tracking-wider text-white placeholder:text-white/40 focus:outline-none focus:border-brand-rose focus:bg-white/[0.22] focus:ring-[3px] focus:ring-brand-rose/25 disabled:opacity-50"
              />

              <button
                type="submit"
                disabled={!recoveryCode.trim() || isLoading}
                className="w-full py-2.5 rounded-lg text-white font-semibold text-sm shadow-lg bg-gradient-to-r from-brand-rose to-brand-orange hover:from-brand-purple hover:to-brand-rose hover:-translate-y-px hover:shadow-[0_8px_25px_rgba(237,18,95,0.4)] transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0 flex items-center justify-center gap-2"
              >
                {isLoading && (
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                )}
                {isLoading ? t('twoFactor.recoveryVerifying') : t('twoFactor.recoverySubmit')}
              </button>
            </form>
          )}

          {/* Recovery & help links */}
          <div className="space-y-3 pt-2">
            <div className="text-center">
              <button
                type="button"
                onClick={() => {
                  setMode(mode === 'totp' ? 'recovery' : 'totp');
                  setError('');
                  setDigits(['', '', '', '', '', '']);
                  setRecoveryCode('');
                }}
                className="text-sm font-medium text-brand-rose hover:text-brand-orange transition-colors"
              >
                {mode === 'totp' ? t('twoFactor.useRecovery') : t('twoFactor.useAuthenticator')}
              </button>
            </div>
            <div className="text-center">
              <button type="button" onClick={() => toast.info(t('twoFactor.contactAdmin'))} className="text-xs text-white/40 hover:text-white/60 transition-colors">
                {t('twoFactor.needHelp')}
              </button>
            </div>
          </div>

          {/* Back to login */}
          <div className="pt-2">
            <button
              type="button"
              onClick={handleBackToLogin}
              className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium text-white/80 hover:text-white bg-white/10 border border-white/[0.28] backdrop-blur-[10px] hover:bg-white/[0.18] hover:border-white/40 hover:-translate-y-px transition-all duration-300"
            >
              <ArrowLeft className="w-4 h-4" />
              {t('twoFactor.backToLogin')}
            </button>
          </div>
        </div>
      </div>

      {/* Bottom copyright */}
      <div className="absolute bottom-6 left-0 right-0 text-center z-10">
        <p className="text-white/25 text-xs">{t('common:footer.copyright', { year: new Date().getFullYear() })}</p>
      </div>
    </div>
  );
}
